import { useEffect, useMemo, useState } from "react";
import { BottomSpacer, Box, VBox } from "@/components-v2/Box";
import { BatchSigningController } from "./batchSigningDomain";
import { observer } from "mobx-react";
import { fromQueryString, singleValue } from "@/utils/queryParams";
import { when } from "@/utils/functions";
import { Select } from "@/components-v2/Select";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import {
  DoneStateUIState,
  prepareState,
  ReadyUIState,
  SignDocumentPageUIState,
} from "./signDocumentDomain";
import { fromParametertoSigningMethod } from "@/utils/navigation";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import {
  SignWithEmailView,
  SignWithHandwritingView,
  SigningAlert,
  SocialSecurityNumberInput,
} from "./signingComponents";
import { Alert } from "@/components-v2/Alert";
import { PdfPreview } from "@/components-v2/PdfPreview";
import parse from "html-react-parser";
import { Dialog } from "@/components-v2/Dialog";
import { Checkbox } from "@/components-v2/Checkbox";
import { useLanguage, useRootStore, useTranslation, useUserStore } from "@/stores/RootStoreContext";
import { useAppBar } from "@/appBar/useAppBar";

export const BatchSigningPage = observer(function BatchSigningPage({
  signingMethod,
}: {
  signingMethod: string;
}) {
  const rootStore = useRootStore();
  const location = useRootStore().location;
  const lang = useLanguage();
  const appBar = useAppBar();
  const controller = useMemo(
    () =>
      new BatchSigningController(
        rootStore.userStore,
        rootStore.apiService,
        rootStore.dispatch,
        rootStore,
        fromParametertoSigningMethod(signingMethod),
        location.pathname,
      ),
    [rootStore, signingMethod, location.pathname],
  );

  const queryParams = useMemo(() => fromQueryString(location.search), [location.search]);
  useEffect(() => {
    controller.init(
      location.pathname,
      singleValue(queryParams.ref),
      singleValue(queryParams.batchId),
      singleValue(queryParams.mode),
    );
  }, [controller, location.pathname, queryParams]);
  return (
    <FullScreenLayout
      pageHeader={
        <BatchSigningHeader
          controller={controller}
          onClosePage={rootStore.userStore.closeBatchSigningPage}
        />
      }
      alertStore={rootStore.messageStore}
      centerPanel={
        <BatchSigningView
          state={prepareState(rootStore.dispatch, rootStore.signDocument, appBar, lang)}
        />
      }
    />
  );
});

const BatchSigningHeader = observer(function BatchSigningHeader({
  controller,
  onClosePage,
}: {
  controller: BatchSigningController;
  onClosePage: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Box
      bg={"default"}
      grow
      alignItems={"center"}
      paddingLeft={"md"}
      paddingRight={"lg"}
      paddingY={"md"}
      borderBottom
    >
      <IconButton icon={"x"} tooltip={t("back")} onClick={onClosePage} />
      <Box>
        {controller.showSuccess
          ? t("signingDialog.successHeader")
          : when(controller.readyHeader, t)}
      </Box>
      <Box grow justifyContent={"end"}>
        {controller.showNavigationButtons && (
          <Box>
            <Select
              value={controller.selectedDocument}
              items={controller.batchDocuments}
              onSelect={(item) => controller.selectDocument(item)}
              idFn={(document) => document.documentReference}
              labelFn={(document) => document.documentTitle}
            />
            <Button
              variant={"basic"}
              icon={"caret-left"}
              tooltip={t("previous")}
              onClick={controller.previousDocument}
            />
            <Button
              variant={"basic"}
              icon={"caret-right"}
              tooltip={t("next")}
              onClick={controller.nextDocument}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
});

const BatchSigningView = observer(function BatchSigningView({
  state,
}: {
  state: SignDocumentPageUIState;
}) {
  const render = () => {
    switch (state.type) {
      case "loading":
        return <LoadingIndicator fullscreen />;
      case "ready":
        return <BatchReadyView {...state} />;
      case "sign-with-email":
        return <SignWithEmailView {...state} />;
      case "sign-with-handwriting":
        return <SignWithHandwritingView {...state} />;
      case "done":
        return <BatchDoneView {...state} />;
    }
  };

  return (
    <VBox>
      <VBox>{render()}</VBox>
    </VBox>
  );
});

function DocumentPreview({
  document,
  onDownload,
  isDownloading,
}: {
  document: { type: "pdf"; url: string } | { type: "html"; content: string };
  onDownload?: () => void;
  isDownloading?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box justifyContent={"center"}>
      {document.type === "pdf" && (
        <Box className={"relative"}>
          {onDownload && (
            <Box className={"absolute left-8 top-4 z-10"}>
              <LinkButton
                label={t("download")}
                icon={"download"}
                onClick={onDownload}
                loading={isDownloading}
              />
            </Box>
          )}
          <PdfPreview file={document} />
        </Box>
      )}
      {document.type === "html" && parse(document.content, { trim: true })}
    </Box>
  );
}

export const BatchReadyView = observer(
  ({
    title,
    description,
    document,
    alert,
    socialSecurityNumber,
    startSigning,
    startBatchSigning,
    downloadDocument: downloadPreview,
    isDownloadingDocument: isDownloadingPreview,
  }: ReadyUIState) => {
    const { t } = useTranslation();
    const userStore = useUserStore();
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

    return (
      <>
        <Alert
          type={"info"}
          paddingY={"sm"}
          message={
            <Box grow justifyContent={"center"}>
              <strong>{t(title)}</strong>
              <span>{t(description)}</span>
            </Box>
          }
        />
        {alert && <SigningAlert alert={alert} />}
        {document && (
          <DocumentPreview
            document={document}
            onDownload={downloadPreview}
            isDownloading={isDownloadingPreview}
          />
        )}
        <BottomSpacer />
        <Box sticky={"bottom"} justifyContent={"center"} padding bg borderTop className={"z-10"}>
          <Button
            variant={"secondary"}
            label={t("cancel")}
            onClick={() => userStore.closeBatchSigningPage()}
          />
          {socialSecurityNumber && (
            <Box className={"max-w-80"} grow>
              <SocialSecurityNumberInput
                socialSecurityNumber={socialSecurityNumber}
                startSigning={startSigning}
              />
            </Box>
          )}
          {startBatchSigning ? (
            <>
              <Button variant={"secondary"} {...startSigning} />
              <Button
                variant={"primary"}
                label={t(startBatchSigning.label)}
                onClick={() => setShowConfirmationDialog(true)}
              />
            </>
          ) : (
            <Button variant={"primary"} {...startSigning} />
          )}
        </Box>

        {startBatchSigning && showConfirmationDialog && (
          <ConfirmBatchSigningDialog
            onSubmit={() => {
              setShowConfirmationDialog(false);
              startBatchSigning.onClick();
            }}
            onCancel={() => {
              setShowConfirmationDialog(false);
            }}
          />
        )}
      </>
    );
  },
);

function ConfirmBatchSigningDialog({
  onCancel,
  onSubmit,
}: {
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [batchSigningConfirmed, setBatchSigningConfirmed] = useState(false);

  return (
    <Dialog
      title={t("batchSigning.confirmHeader")}
      maxWidth={"sm"}
      onSubmit={onSubmit}
      submitLabel={t("batchSigning.continueSigning")}
      isSubmitDisabled={!batchSigningConfirmed}
      onCancel={() => {
        onCancel();
        setBatchSigningConfirmed(false);
      }}
    >
      <VBox>
        <p>{t("batchSigning.confirmText")}</p>
        <Checkbox
          label={t("batchSigning.confirmCheckbox")}
          checked={batchSigningConfirmed}
          onChange={(e) => setBatchSigningConfirmed(e.target.checked)}
        />
      </VBox>
    </Dialog>
  );
}

export const BatchDoneView = observer(
  ({
    title,
    description,
    document,
    alertType,
    downloadDocument,
    isDownloadingDocument,
  }: DoneStateUIState) => {
    const { t } = useTranslation();
    return (
      <>
        <Alert
          type={alertType}
          paddingY={"sm"}
          message={
            <Box justifyContent={"center"}>
              <strong>{t(title)}</strong>
              <span>{description && t(description)}</span>
            </Box>
          }
        />
        {document && (
          <DocumentPreview
            document={document}
            onDownload={downloadDocument}
            isDownloading={isDownloadingDocument}
          />
        )}
      </>
    );
  },
);
